import React, { useState } from 'react';

import type { FeatureCategories } from 'src/modules/designStudio/constants';
import { ColorGroupLabelColors } from 'src/modules/designStudio/constants/hardCodedData/colorData';
import type { UpdateDesignFunction } from 'src/modules/designStudio/providers/HistoryProvider';
import type { LegacyDesignConfig } from 'src/modules/designStudio/types';
import type { DesignStudioColor } from 'src/shared/graphql/generated';
import { useTranslate } from 'src/shared/hooks';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';
import { Page, buttonPressed } from 'src/shared/telemetry';

import { ColorPicker } from '../../ColorPicker';

const FILTER_TITLE = 'Colors';

export const ColorList = ({
  color,
  designConfigCategory,
  updateDesign,
  designConfig,
  subHeader,
  colorData,
  colorGroupLabelColors = ColorGroupLabelColors,
}: {
  color: string | null;
  designConfigCategory: FeatureCategories;
  updateDesign: UpdateDesignFunction;
  designConfig: LegacyDesignConfig;
  colorData: DesignStudioColor[];
  colorGroupLabelColors?: { [key: string]: string };
  subHeader?: React.ReactNode;
}) => {
  const selectedColor = colorData.find(item => item.color === color);
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);
  const initialColorGroup = selectedColor?.group || colorData[0]?.group;
  const [selectedColorGroup, setColorGroup] = useState(initialColorGroup);

  const changeColor = (selectedColorItem: DesignStudioColor) => {
    updateDesign({
      selectedColor: selectedColorItem,
      category: designConfigCategory,
      update: {
        ...designConfig[designConfigCategory],
        color: selectedColorItem.color,
      },
      trackingData: {
        product: designConfigCategory,
        parameter: FILTER_TITLE,
        selection: selectedColorItem.color,
      },
    });
  };

  const selectColor = (newColor: DesignStudioColor) => {
    const buttonPressedTrackingData = {
      button_text: newColor.color,
      item_type: 'color',
      page_or_screen_name: Page.DESIGN_STUDIO,
      primary_cta: false,
    };

    changeColor(newColor);
    buttonPressed(buttonPressedTrackingData);
  };

  const items = colorData.filter(item => item.group === selectedColorGroup);
  const groupSet = new Set<string>();

  colorData.forEach(item => groupSet.add(item.group));

  const groups = Array.from(groupSet);

  return (
    <ColorPicker
      changeColor={selectColor}
      groupLabelColors={colorGroupLabelColors}
      groups={groups}
      header={t('menu.filters.color')}
      items={items}
      selectedColor={selectedColor?.color}
      selectedColorName={selectedColor?.name}
      selectedGroup={selectedColorGroup}
      setColorGroup={setColorGroup}
      subHeader={subHeader}
    />
  );
};
